/**
 * Operação > Visão do cliente (spec 060; Vitor, 23/10): liga e desliga partes da área do cliente, pra todos os
 * clientes deste ambiente. Cada grupo tem o interruptor dele ("Financeiro inteiro"); desligado, esconde as partes e,
 * religado, cada uma volta como estava.
 *
 * A trava e do backend (5 s entre trocas; 10 trocas em 2 minutos param 1 minuto): a tela so desliga os interruptores
 * e conta o tempo que falta, pela hora do servidor.
 */
import { Aviso, CabecalhoDePagina, Carregando, ErroApi, Interruptor, Pilha, Secao, Tabela, Vazio, formatarDataHora } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { erroDe, useApi, usePapel } from "../api";

type Parte = { chave: string; rotulo: string; descricao: string; visivel: boolean };
type Grupo = { chave: string; rotulo: string; visivel: boolean; partes: Parte[] };
type Troca = { em: string; rotulo: string; visivel: boolean; quem: string; bloqueou: boolean };
type Painel = { grupos: Grupo[]; libera_em: string | null; bloqueio: boolean; agora: string; ultimas: Troca[] };

const CHAVE = ["visao-do-cliente"];

/** Segundos ate liberar, pela hora do servidor (o relogio do computador de quem olha pode estar adiantado). */
function useFaltam(p: Painel | undefined) {
  const [, tique] = useState(0);
  const [diferenca, setDiferenca] = useState(0);
  useEffect(() => { if (p) setDiferenca(new Date(p.agora).getTime() - Date.now()); }, [p]);
  const faltam = p?.libera_em ? Math.ceil((new Date(p.libera_em).getTime() - (Date.now() + diferenca)) / 1000) : 0;
  useEffect(() => {
    if (faltam <= 0) return;
    const t = setInterval(() => tique((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [faltam > 0]);
  return Math.max(0, faltam);
}

const emMinutos = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : `${s} s`);

export function PaginaVisaoDoCliente() {
  const { ler, por } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: CHAVE, queryFn: () => ler<Painel>("/v1/admin/visao-do-cliente") });
  // A troca em andamento: o interruptor ja mostra a posicao nova enquanto o servidor responde (se recusar, volta).
  const [trocando, setTrocando] = useState<{ chave: string; visivel: boolean } | null>(null);
  const [erro, setErro] = useState<string>();
  const faltam = useFaltam(q.data);
  const admin = pode("ADMIN");
  const parado = faltam > 0;

  const trocar = async (chave: string, visivel: boolean) => {
    setErro(undefined);
    setTrocando({ chave, visivel });
    try {
      qc.setQueryData(CHAVE, await por<Painel>(`/v1/admin/visao-do-cliente/${encodeURIComponent(chave)}`, { visivel }));
    } catch (e) {
      // Recusa da trava (429) volta com a hora em que libera: a tela relê e passa a contar.
      if (!(e instanceof ErroApi && e.status === 429)) setErro(erroDe(e));
      await qc.invalidateQueries({ queryKey: CHAVE });
    } finally {
      setTrocando(null);
    }
  };

  const cabecalho = <CabecalhoDePagina titulo="Visão do cliente" descricao="O que a área do cliente mostra, pra todos os clientes deste ambiente. Desligado, some do menu, das abas e dos atalhos do Início na próxima vez que a pessoa abrir ou recarregar a área. Início, Perfil e Notificações sempre aparecem." />;
  if (q.isLoading) return <Pilha espaco={5}>{cabecalho}<Carregando compacto texto="Buscando o que a área mostra" /></Pilha>;
  if (!q.data) return <Pilha espaco={5}>{cabecalho}<Aviso tipo="erro">{erroDe(q.error)}</Aviso></Pilha>;
  const p = q.data;
  const travado = !admin || parado || trocando !== null;
  const estado = (chave: string, visivel: boolean) => (trocando?.chave === chave ? trocando.visivel : visivel);

  return (
    <Pilha espaco={5}>
      {cabecalho}
      {!admin && <Aviso tipo="info">Ligar e desligar é do administrador. Você vê o que a área mostra agora.</Aviso>}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {parado && (p.bloqueio
        ? <Aviso tipo="alerta">Muitas trocas seguidas: as trocas param 1 minuto. Voltam em {emMinutos(faltam)}.</Aviso>
        : <Aviso tipo="info">Próxima troca em {emMinutos(faltam)}. Entre uma troca e outra, 5 segundos.</Aviso>)}
      {p.grupos.map((g) => (
        <Secao key={g.chave} titulo={g.rotulo} acoes={<Interruptor rotulo={`${g.rotulo} inteiro`} ligado={estado(g.chave, g.visivel)} disabled={travado} aoMudar={(v) => void trocar(g.chave, v)} />}>
          <div style={{ display: "grid", gap: "var(--e-4)" }}>
            {g.partes.map((parte) => (
              <Interruptor
                key={parte.chave}
                rotulo={parte.rotulo}
                dica={g.visivel ? parte.descricao : `${parte.descricao} Escondida com ${g.rotulo} inteiro.`}
                ligado={estado(g.chave, g.visivel) && estado(parte.chave, parte.visivel)}
                disabled={travado || !g.visivel}
                aoMudar={(v) => void trocar(parte.chave, v)}
              />
            ))}
          </div>
        </Secao>
      ))}
      <Secao titulo="Últimas trocas">
        <Tabela
          linhas={p.ultimas}
          chave={(t) => `${t.em}-${t.rotulo}`}
          vazio={<Vazio titulo="Nenhuma troca ainda">Tudo aparece pro cliente, como veio.</Vazio>}
          colunas={[
            { chave: "em", rotulo: "Quando", celula: (t) => <span className="mtc-mono">{formatarDataHora(t.em)}</span> },
            { chave: "o-que", rotulo: "O que", celula: (t) => <>{t.visivel ? "Ligou" : "Desligou"} {t.rotulo}{t.bloqueou && <span className="mtc-tabela__apoio">Décima troca seguida: parou as trocas por 1 minuto.</span>}</> },
            { chave: "quem", rotulo: "Por", celula: (t) => t.quem },
          ]}
        />
      </Secao>
    </Pilha>
  );
}
